import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Message } from "../models/message";
import { Question } from "../models/question";
import { ConfigurationLoader } from "./configuration-loader.service";
import { catchError, map } from 'rxjs/operators';
import { ContentType } from "../models/content";
import { TextContent } from "../models/text-content";
import { OptionsContent } from "../models/options-content";
import { Observable, Subject, throwError } from "rxjs";
import { ImageContent } from "../models/image-content";
import { Parameter } from "../models/parameter";

/**
 * Serviço que se comunica com o Gateway do Serprobots.
 * Depende da configuração para obter o endereço do Serviço.
 */
@Injectable({ providedIn: "root" })
export class Gateway {
    private baseURL?: string;
    private isSendingSubject: Subject<boolean> = new Subject<boolean>();

    constructor(
        private http: HttpClient,
        private configurationLoader: ConfigurationLoader
    ) {
    }

    /**
     * Observable que informa quando este serviço está se comunicando com a API
     * para obter uma resposta a uma pergunta.
     * 
     * Pode ser utilizado pela UI para exibir alguma mensagem de "ocupado" para o usuário.
     * 
     * @returns Observable.
     */
    isSending(): Observable<boolean> {
        return this.isSendingSubject.asObservable();
    }

    /**
     * Carrega o endereço do serviço onde devem ser realizadas as chamadas.
     * Carrega uma única vez e guarda para futuras referências.
     */
    private async loadBaseURL(): Promise<void> {
        if (!this.baseURL) {
            this.baseURL = (await this.configurationLoader.getConfiguration()).serviceURL;
        }
    }

    /**
     * Finaliza uma conversação.
     * 
     * @param conversationId Conversação que deve ser finalizada.
     */
    async deleteConversation(conversationId: string): Promise<void> {
        await this.loadBaseURL();

        const httpOptions = { headers: new HttpHeaders({ accept: 'application/json', 'ContentType': 'application/json' }) };
        
        this.isSendingSubject.next(true);

        const result = await this.http.delete<void>(`${this.baseURL}/conversation/${conversationId}`, httpOptions).toPromise();

        this.isSendingSubject.next(false);

        return result;
    }

    /**
     * Comunica-se com o Serviço/Gateway responsável pela criação do ID da conversação.
     * O ID retornado é uma string contendo um UUID que está no formato UUIDv4.
     * 
     * @returns UUID que representa o ID da conversação.
     */
    async createConversationId(queryParams: any): Promise<string> {
        await this.loadBaseURL();

        // O Gateway sempre retorna um text/plain com o ID da conversação.
        // É necessário informar isso aqui com "responseType" = text para o HttpClient não tentar fazer um parse de um JSON.
        // O "text" as "text" foi usado, pois, aparentemente, o HttpClient só funciona se for informado desta maneira.
        const httpOptions = { headers: new HttpHeaders({accept: 'text/plain'}), responseType: "text" as "text" };

        return this.http.post(`${this.baseURL}/conversation/`, { parameters: queryParams }, httpOptions).toPromise();
    } 

    /**
     * Obtém todas as mensagens, ordenadas por data, de uma determinada conversação.
     * 
     * @param moduleId Módulo do qual serão obtidas as mensagens do histórico.
     * @param conversationId Conversação da qual serão obtidas as mensagens.
     */
    async getConversationHistory(conversationId: string): Promise<Message[]> {
        await this.loadBaseURL();
        const httpOptions = { headers: new HttpHeaders({ accept: 'application/json', 'ContentType': 'application/json' }) };

        this.isSendingSubject.next(true);

        return this.http.get<Message[]>(`${this.baseURL}/conversation/${conversationId}/messages`, httpOptions)
            .pipe(map((messages: Message[]) => { 
                this.isSendingSubject.next(false);
                return messages.map( m => this.convert(m) );
            }), catchError(err => {
                this.isSendingSubject.next(false);
                return throwError(err);
            })).toPromise();
    }

    /**
     * Comunica-se com o Serviço/Gateway responsável em dar uma resposta a uma determinada pergunta.
     * 
     * @param question Pergunta que será enviada para o Gateway.
     * @returns Mensagem contendo a resposta do Gateway.
     */
    async answer(question: Question): Promise<Message> {
        await this.loadBaseURL();
        const httpOptions = { headers: new HttpHeaders({ accept: 'application/json', 'ContentType': 'application/json' }) };
        
        this.isSendingSubject.next(true);

        const result = await this.http
            .post<Message>(`${this.baseURL}/answer/`, question, httpOptions)
            .pipe(map((message: Message) => { 
                return this.convert(message);
            }), catchError(err => {
                this.isSendingSubject.next(false);
                return throwError(err);
            })).toPromise();

        this.isSendingSubject.next(false);

        return result;
    }

    /**
     * Converte o objeto obtido da API do Gateway para um objeto Message, que contém as divisões dos Contents.
     * 
     * @param message Objeto obtido da API.
     * @returns Uma Mensagem (Message) criado a partir dos dados do objeto da API.
     */
    private convert(message: any): Message {

        // Ao invés de retornar o objeto convertido pelo HttpClient, fazemos uma conversão para o nosso tipo específico.
        // Typescript usa Structural Typing, portanto, o objeto retornado não será exatamente o nosso mas um
        // que é similar. Este objeto similar não conterá os métodos e demais atributos das classes Message, TextContent, OptionsContent e etc.
        const msg = Object.assign(new Message(), message);
        const newContents = [];

        msg.date = new Date(message.date);
        
        for(const c of msg.contents) {
            let cc = c as any;

            if (cc.type == ContentType.TEXT.toString()) {
                newContents.push(Object.assign(new TextContent(), cc as TextContent));
            } else if (cc.type == ContentType.OPTIONS.toString()) {
                newContents.push(Object.assign(new OptionsContent(), cc as OptionsContent));
            } else if (cc.type == ContentType.IMAGE.toString()) {
                newContents.push(Object.assign(new ImageContent(), cc as ImageContent));
            }
        }

        const uiParams: Parameter[] = [];

        for(const key in message.ui) {
            uiParams.push(new Parameter(key, message.ui[key] as any));
        }
        
        msg.ui = uiParams;
        msg.contents = newContents;

        return msg;
    }
        
}